import React from 'react';
import {I18n, Translate} from 'react-redux-i18n';
import PropTypes from 'prop-types';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import {isEmpty} from 'front-onceforall-core/dist/utils/arrays';
import {Collapse} from '@material-ui/core';
import {EntityIllustration, Icon} from 'front-onceforall-core';
import * as snackbarsActions from '../../actions/snackbars';
import {translateToLocale} from '../../translations';
import {interventionRole, INVITATION_TYPE} from '../../utils/constant';
import OrganisationInvitation from '../organisationInvitation';
import interventionService from '../../services/intervention/InterventionService';
import organisationService from '../../services/organisation/OrganisationService';
import enrollmentService from '../../services/enrollment';
import {getOrganisationLogoUrl} from '../../utils/organisation';
import {captureError} from '../../utils/log';
import ExternalInvitationSuccess from '../externalInvitationSuccess';

export class InterventionAddForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      roles: [],
      roleSelected: null,
      invitation: null,
      submitted: false,
      isModalOpen: false
    };

    this.isInterventionRole = this.isInterventionRole.bind(this);
    this.getAvailableRoles = this.getAvailableRoles.bind(this);
    this.getDefaultInterventionRole = this.getDefaultInterventionRole.bind(this);
    this.reset = this.reset.bind(this);
    this.handleSelectInternalInvitation = this.handleSelectInternalInvitation.bind(this);
    this.handleExternalInvitation = this.handleExternalInvitation.bind(this);
    this.sendInternalInvitation = this.sendInternalInvitation.bind(this);
    this.sendExternalInvitation = this.sendExternalInvitation.bind(this);
    this.sendInvitation = this.sendInvitation.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.translateInvitationMessage = this.translateInvitationMessage.bind(this);
  }

  componentDidMount() {
    this.setState({roles: this.getAvailableRoles()});
  }

  isInterventionRole(role) {
    return !isEmpty(this.props.interventions
      .filter(intervention => intervention.organisation.organisationId === this.props.organisation.organisationId)
      .filter(intervention => intervention.role === role));
  }

  getAvailableRoles() {
    const allInterventions = this.props.interventions;
    const hasClient = allInterventions.some(intervention => intervention.role === interventionRole.CLIENT);
    const isProjectOwner = this.props.project.ownerOrganisation.organisationId === this.props.organisation.organisationId;
    const isClient = this.isInterventionRole(interventionRole.CLIENT);
    const isServiceProvider = this.isInterventionRole(interventionRole.SERVICE_PROVIDER);
    const isSubContractor = this.isInterventionRole(interventionRole.SUB_CONTRACTOR);
    const isProjectManager = this.isInterventionRole(interventionRole.PROJECT_MANAGER_CLIENT) ||
      this.isInterventionRole(interventionRole.PROJECT_MANAGER);
    return [
      {
        key: interventionRole.CLIENT,
        disabled: (hasClient || !isProjectOwner),
        externalInvitationOnNoDepositor: false
      },
      {
        key: interventionRole.GENERAL_CONTRACTOR,
        disabled: !isProjectOwner && !isClient && !isProjectManager,
        externalInvitationOnNoDepositor: true
      },
      {
        key: interventionRole.PROJECT_MANAGER_CLIENT,
        disabled: isSubContractor || isServiceProvider,
        externalInvitationOnNoDepositor: false
      },
      {
        key: interventionRole.SERVICE_PROVIDER,
        disabled: false,
        externalInvitationOnNoDepositor: true
      },
      {
        key: interventionRole.SUB_CONTRACTOR,
        disabled: isClient || isServiceProvider,
        externalInvitationOnNoDepositor: true
      }
    ].filter(role => !role.disabled);
  }

  getDefaultInterventionRole() {
    if (this.isInterventionRole(interventionRole.CLIENT)) {
      return interventionRole.GENERAL_CONTRACTOR;
    }

    if (this.isInterventionRole(interventionRole.SERVICE_PROVIDER)) {
      return interventionRole.SERVICE_PROVIDER;
    }

    return interventionRole.SUB_CONTRACTOR;
  }

  reset(step = 0) {
    let state = {invitation: null};
    if (step < 1) {
      state = {roleSelected: null, ...state};
    }

    this.setState(state);
  }

  handleSelectInternalInvitation(organisation) {
    return organisationService.get(organisation.organisationId)
      .then(response => {
        if (organisation.depositor || !this.state.roleSelected.externalInvitationOnNoDepositor) {
          const invitation = {
            type: INVITATION_TYPE.INTERNAL,
            organisation: {...organisation, priorAcceptation: response.priorAcceptation},
            role: this.getDefaultInterventionRole()
          };
          this.setState({
            invitation
          });
        }
      })
      .catch(error => {
        captureError('Error while searching for intervention', error);
        this.props.actions.addError(<Translate value="error.generic"/>);
        return Promise.reject(new Error('error.generic'));
      });
  }

  handleExternalInvitation(organisation) {
    const invitation = {
      type: INVITATION_TYPE.EXTERNAL,
      organisation,
      role: this.getDefaultInterventionRole()
    };
    this.setState({
      invitation
    });

    return Promise.resolve();
  }

  sendInternalInvitation() {
    return interventionService.addInterventions({
      fromEnrollment: false,
      role: this.state.roleSelected.key,
      projectId: this.props.project.projectId,
      organisationId: this.state.invitation.organisation.organisationId,
      emitterOrganisationInterventionId: this.props.myIntervention.interventionId
    });
  }

  makeExternalInvitationDataObject() {
    const {
      organisation: invitingOrganisation,
      project: {projectId},
      myIntervention
    } = this.props;
    const {
      invitation: {organisation: invitedOrganisation},
      roleSelected: {key: roleSelectedKey}
    } = this.state;

    return {
      invitingCountry: invitingOrganisation.country,
      emitterOrganisationId: invitingOrganisation.organisationId,
      organisationName: invitedOrganisation.organisationName,
      email: invitedOrganisation.email,
      phoneNumber: invitedOrganisation.phoneNumber,
      firstName: invitedOrganisation.firstName,
      lastName: invitedOrganisation.lastName,
      customMessage: invitedOrganisation.customMessage,
      language: invitedOrganisation.language,
      registrationNumber: invitedOrganisation.registrationNumber,
      vatIdentificationNumber: invitedOrganisation.vatIdentificationNumber,
      country: invitedOrganisation.country,
      targetRole: roleSelectedKey,
      projectId,
      emitterOrganisationInterventionId: myIntervention.interventionId,
      sourceRole: myIntervention.role
    };
  }

  sendExternalInvitation() {
    const data = this.makeExternalInvitationDataObject();
    return enrollmentService.project.inviteOrganisations(
      data.emitterOrganisationId,
      [data]
    );
  }

  sendInvitation(isExternalInvitation) {
    return isExternalInvitation ? this.sendExternalInvitation() : this.sendInternalInvitation();
  }

  handleSubmit() {
    if (this.state.submitted || !this.state.roleSelected || !this.state.invitation) {
      return;
    }

    this.setState({submitted: true});

    const isExternalInvitation = this.state.invitation.type === INVITATION_TYPE.EXTERNAL;

    this.sendInvitation(isExternalInvitation)
      .then(() => {
        if (isExternalInvitation) {
          this.setState({isModalOpen: true});
        } else {
          this.props.actions.addSuccess(<Translate
            value="project.edit.addOrganisationInternalSuccess"
            name={this.state.invitation.organisation.fullName}
          />);
          this.props.onClose();
        }
      })
      .catch(async error => {
        const errorObject = await error.response.json();
        this.setState({submitted: false});
        this.props.actions.addError(<Translate value={errorObject.message}/>);
      });
  }

  handleCloseModal = () => {
    this.setState({isModalOpen: false});
    this.props.onClose();
  };

  translateInvitationMessage(language = this.props.currentLocale) {
    const customMessageCode = this.state.roleSelected.key === interventionRole.CLIENT ? 'project.invite.client.defaultMessage' : 'project.invite.depositor.defaultMessage';

    return translateToLocale(language, customMessageCode, {
      organisationName: this.props.organisation.fullName,
      projectName: this.props.project.name,
      userName: this.props.user.fullName
    });
  }

  renderRoleBox() {
    return (<div className="box">
      <div className="row">
        <div className="col-12 inline-container space-between">
          <div className="inline-container">
            {!this.state.roleSelected &&
              <h3 className="my-1">
                <Translate value="project.edit.roleType"/>
              </h3>
            }
            {this.state.roleSelected &&
            <h3 className="my-1 inline-container">
              <Translate value="project.edit.roleAs"/>
              <div
                data-id="role-selected-view"
                className="chip information-primary"
              >
                <Translate value={`intervention.role.${this.state.roleSelected.key}`}/>
              </div>
            </h3>
            }
          </div>

          {this.state.roleSelected &&
            <button
              data-id="button-change-role"
              type="button"
              className="small secondary"
              onClick={() => this.reset()}
            >
              <Translate value="project.edit.changeRole"/>
            </button>
          }
        </div>
      </div>
      <Collapse
        data-id="roles-collapse"
        in={!this.state.roleSelected}
      >
        <div className="row mt-4">
          {this.state.roles.map((role, index) => (<div
            key={index}
            data-id="role-box"
            className="col-md-6 mt-2"
          >
            <div
              data-id={`role-box-${role.key}`}
              className="box clickable"
              onClick={() => this.setState({roleSelected: role})}
              style={{height: '100%'}}
            >
              <h4 className="m-0 information-primary">
                <Translate value={`intervention.role.${role.key}`}/>
              </h4>
              <p
                dangerouslySetInnerHTML={{
                  __html: I18n.t(`project.edit.descriptionsPerRole.${role.key}`, {interpolation: {escapeValue: false}})
                }}
              />
            </div>
          </div>))}
        </div>
      </Collapse>
    </div>);
  }

  renderOrganisationSelectionBox() {
    return (<div className="box mt-4">
      <div className="row">
        <div className="col-12 inline-container space-between">
          {!this.state.invitation && <h3 className="my-1">
            <Translate value="project.edit.organisation"/>
          </h3>}
          {this.state.invitation &&
          <>
            <h3 className="my-1">
              <Translate value={`project.edit.message.${this.state.invitation.type}`}/>
            </h3>
            <button
              data-id="cancel-organisation-selected"
              type="button"
              className="small secondary"
              onClick={() => this.reset(1)}
            >
              <Translate value="project.edit.changeOrganisation"/>
            </button>
          </>
          }
        </div>
      </div>
      <Collapse
        data-id="organisation-selection-collapse"
        in={!this.state.invitation}
      >
        {!this.state.invitation &&
          <div className="mt-4">
            <OrganisationInvitation
              externalInvitationOnNoDepositor={this.state.roleSelected.externalInvitationOnNoDepositor}
              onSelectInternalInvitation={this.handleSelectInternalInvitation}
              onExternalInvitation={this.handleExternalInvitation}
              onTranslateInvitationMessage={this.translateInvitationMessage}
            />
          </div>
        }
      </Collapse>
      {this.state.invitation && this.renderOrganisation(this.state.invitation)}
    </div>);
  }

  renderOrganisation(invitation) {
    const {organisation} = invitation;
    return (<div
      data-id="render-organisation"
      className="row"
    >
      <div className="col-12">
        <hr className="mt-4"/>
        <div className="mt-4 flex-container">
          <div className="col-sm-2 inline-container column justify-content-center">
            <EntityIllustration
              country={organisation.country}
              name={organisation.fullName || organisation.organisationName}
              imageUrl={getOrganisationLogoUrl(organisation.organisationId)}
              small
            />
          </div>
          <div className="col-sm-5 inline-container column justify-content-center">
            <strong data-id="fullName">{organisation.fullName || organisation.organisationName}</strong>
            <span data-id="registrationNumber">{organisation.registrationNumber}</span>
          </div>
          <div className="col-sm-5 inline-container column justify-content-center">
            {organisation.address ? <>
              <span data-id="addressLine">{organisation.address.addressLine}</span>
              <span data-id="city">{organisation.address.city}</span>
            </> : <>
              <span data-id="name">{invitation.organisation.firstName} {invitation.organisation.lastName}</span>
              <span data-id="email">{invitation.organisation.email}</span>
            </>}
          </div>
        </div>
      </div>
    </div>);
  }

  render() {
    return (
      <>
        {this.state.submitted && <div className="loading-overlay"/>}
        <div className="flex-container column space-between">
          <div>
            <h2 className="mb-4"><Translate value="project.edit.title"/></h2>
            {this.renderRoleBox()}
            {this.state.roleSelected && this.renderOrganisationSelectionBox()}
          </div>

          <div className="mt-5 col-12 inline-container space-between">
            <button
              type="button"
              data-id="cancel"
              className="large secondary inline-container"
              onClick={() => this.props.onClose()}
            >
              <Icon icon="go-back"/>
              <Translate value="action.cancel"/>
            </button>

            <button
              className="large primary inline-container"
              type="button"
              data-id="submit"
              onClick={this.handleSubmit}
              disabled={!this.state.roleSelected || !this.state.invitation || this.state.submitted}
            >
              <Icon icon={this.state.submitted ? 'loader' : 'check'}/>
              <Translate value="action.confirm"/>
            </button>
          </div>
        </div>
        <ExternalInvitationSuccess
          isOpen={this.state.isModalOpen}
          onClose={this.handleCloseModal}
          text={<Translate value="project.edit.externalInvitationSuccess"/>}
        />
      </>);
  }
}

InterventionAddForm.propTypes = {
  interventions: PropTypes.array,
  organisation: PropTypes.object.isRequired,
  project: PropTypes.object.isRequired,
  myIntervention: PropTypes.object.isRequired,
  currentLocale: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired
};

InterventionAddForm.defaultProps = {
  interventions: []
};

function mapStateToProps(state) {
  return {
    organisation: state.organisation,
    currentLocale: state.i18n.locale,
    user: state.user
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({...snackbarsActions}, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(InterventionAddForm);
